import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector, {
  CustomDetector,
} from "i18next-browser-languagedetector";
import { detectPreferredLng } from "./app/languageDetection";

// Import JSON statically (CRA-friendly)
import enHeader from "./locales/en/header.json";
import daHeader from "./locales/da/header.json";
import enFooter from "./locales/en/footer.json";
import daFooter from "./locales/da/footer.json";
import enHome from "./locales/en/home.json";
import daHome from "./locales/da/home.json";
import enAbout from "./locales/en/about.json";
import daAbout from "./locales/da/about.json";
import enServicesOverview from "./locales/en/servicesOverview.json";
import daServicesOverview from "./locales/da/servicesOverview.json";
import enWebsites from "./locales/en/websites.json";
import daWebsites from "./locales/da/websites.json";
import enCustomSolutions from "./locales/en/custom-solutions.json";
import daCustomSolutions from "./locales/da/custom-solutions.json";
import enMobileApps from "./locales/en/mobileApps.json";
import daMobileApps from "./locales/da/mobileApps.json";
import enBooking from "./locales/en/booking.json";
import daBooking from "./locales/da/booking.json";
import enWebshop from "./locales/en/webshop.json";
import daWebshop from "./locales/da/webshop.json";
import enCases from "./locales/en/cases.json";
import daCases from "./locales/da/cases.json";
import enArmring from "./locales/en/case_armring.json";
import daArmring from "./locales/da/case_armring.json";
import enMKcosmetics from "./locales/en/case_mkcosmetics.json";
import daMKcosmetics from "./locales/da/case_mkcosmetics.json";
import enMceIt from "./locales/en/case_mce_it.json";
import daMceIt from "./locales/da/case_mce_it.json";
import enDiagramEditor from "./locales/en/case_diagram_editor.json";
import daDiagramEditor from "./locales/da/case_diagram_editor.json";
import enContact from "./locales/en/contact.json";
import daContact from "./locales/da/contact.json";
import enOnboard from "./locales/en/onboard.json";
import daOnboard from "./locales/da/onboard.json";
import enTrust from "./locales/en/trust.json";
import daTrust from "./locales/da/trust.json";
import enEarlyPartner from "./locales/en/early-partner.json";
import daEarlyPartner from "./locales/da/early-partner.json";

// sv
import svHeader from "./locales/sv/header.json";
import svFooter from "./locales/sv/footer.json";
import svHome from "./locales/sv/home.json";
import svAbout from "./locales/sv/about.json";
import svServicesOverview from "./locales/sv/servicesOverview.json";
import svWebsites from "./locales/sv/websites.json";
import svCustomSolutions from "./locales/sv/custom-solutions.json";
import svMobileApps from "./locales/sv/mobileApps.json";
import svBooking from "./locales/sv/booking.json";
import svWebshop from "./locales/sv/webshop.json";
import svCases from "./locales/sv/cases.json";
import svArmring from "./locales/sv/case_armring.json";
import svMKcosmetics from "./locales/sv/case_mkcosmetics.json";
import svMceIt from "./locales/sv/case_mce_it.json";
import svDiagramEditor from "./locales/sv/case_diagram_editor.json";
import svContact from "./locales/sv/contact.json";
import svOnboard from "./locales/sv/onboard.json";
import svTrust from "./locales/sv/trust.json";
import svEarlyPartner from "./locales/sv/early-partner.json";

const isBrowser = typeof window !== "undefined";

/**
 * Manual resources (add more namespaces as you grow)
 */
const resources = {
  en: {
    header: enHeader,
    footer: enFooter,
    home: enHome,
    about: enAbout,
    servicesOverview: enServicesOverview,
    websites: enWebsites,
    customSolutions: enCustomSolutions,
    booking: enBooking,
    webshop: enWebshop,
    mobileApps: enMobileApps,
    cases: enCases,
    case_armring: enArmring,
    case_mkcosmetics: enMKcosmetics,
    case_mce_it: enMceIt,
    case_diagram_editor: enDiagramEditor,
    contact: enContact,
    onboard: enOnboard,
    trust: enTrust,
    "early-partner": enEarlyPartner,
  },
  da: {
    header: daHeader,
    footer: daFooter,
    home: daHome,
    about: daAbout,
    servicesOverview: daServicesOverview,
    websites: daWebsites,
    customSolutions: daCustomSolutions,
    booking: daBooking,
    webshop: daWebshop,
    mobileApps: daMobileApps,
    cases: daCases,
    case_armring: daArmring,
    case_mkcosmetics: daMKcosmetics,
    case_mce_it: daMceIt,
    case_diagram_editor: daDiagramEditor,
    contact: daContact,
    onboard: daOnboard,
    trust: daTrust,
    "early-partner": daEarlyPartner,
  },
  sv: {
    header: svHeader,
    footer: svFooter,
    home: svHome,
    about: svAbout,
    servicesOverview: svServicesOverview,
    websites: svWebsites,
    customSolutions: svCustomSolutions,
    booking: svBooking,
    webshop: svWebshop,
    mobileApps: svMobileApps,
    cases: svCases,
    case_armring: svArmring,
    case_mkcosmetics: svMKcosmetics,
    case_mce_it: svMceIt,
    case_diagram_editor: svDiagramEditor,
    contact: svContact,
    onboard: svOnboard,
    trust: svTrust,
    "early-partner": svEarlyPartner,
  },
};

/**
 * Custom detector that:
 * 1) respects manual override in localStorage ("lang") if present
 * 2) else uses query (?lang=da/en)
 * 3) else uses browser language, region, and time-zone signals
 * 4) else falls back to English
 */
const customDetector: CustomDetector = {
  name: "dkIpDetector",
  lookup: () => {
    if (!isBrowser) return "en";
    return detectPreferredLng();
  },
  cacheUserLanguage: (lng: string) => {
    // we ONLY cache on manual change; not here.
  },
};

const detector = isBrowser ? new LanguageDetector() : null;
detector?.addDetector(customDetector);

const i18nInstance = detector ? i18n.use(detector) : i18n;

i18nInstance.use(initReactI18next).init({
    resources: resources,
    fallbackLng: "en",
    lng: isBrowser ? undefined : "en",
    ns: [
      "header",
      "footer",
      "home",
      "about",
      "servicesOverview",
      "websites",
      "customSolutions",
      "booking",
      "webshop",
      "mobileApps",
      "cases",
      "case_armring",
      "case_mkcosmetics",
      "case_mce_it",
      "case_diagram_editor",
      "contact",
      "onboard",
      "trust",
      "early-partner",
    ],
    defaultNS: "common",
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // order matters: custom detector first
      order: ["dkIpDetector", "localStorage", "querystring", "navigator"],
      // don't auto-write to localStorage (we handle it manually on dropdown change)
      caches: [],
    },
  });

export default i18n;
